// Pick which matter of the project a time entry or expense belongs to
// (migration 0061). A matter is a named sub-group of a project's work that can
// go out on its own invoice.
//
// Renders nothing until a project is chosen — a matter belongs to one project,
// so callers reset the value to null whenever the project changes. Closed
// matters are left out for new work, except the one already selected, which
// stays visible as "(closed)" so editing an old entry doesn't silently drop it.

import { useState } from 'react';
import { useMatters } from '../api/queries/matters';
import { MatterFormModal } from './MatterFormModal';

const NEW = '__new__';

export interface MatterSelectProps {
  projectId: number | null | undefined;
  value: number | null;
  onChange: (matterId: number | null) => void;
  disabled?: boolean;
  /** Class for the wrapping <label>, so the field matches its form. */
  className?: string;
  /** Inline error from the API (`fieldErrors.matter_id`). */
  error?: string;
  /** No visible caption — for a one-line toolbar such as the timer row. */
  compact?: boolean;
}

export function MatterSelect({
  projectId, value, onChange, disabled, className, error, compact,
}: MatterSelectProps): JSX.Element | null {
  const q = useMatters(projectId);
  const [creating, setCreating] = useState(false);
  if (typeof projectId !== 'number' || projectId <= 0) return null;

  const matters = (q.data?.matters ?? []).filter((m) => m.closed_at === null || m.id === value);

  return (
    <>
      <label className={className}>
        {!compact && <span>Matter</span>}
        <select
          className="form-input"
          value={value === null ? '' : String(value)}
          onChange={(e) => {
            const v = e.target.value;
            if (v === NEW) { setCreating(true); return; }
            onChange(v === '' ? null : Number(v));
          }}
          disabled={disabled || q.isPending}
          aria-label="Matter"
        >
          <option value="">No matter</option>
          {matters.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}{m.closed_at !== null ? ' (closed)' : ''}
            </option>
          ))}
          <option value={NEW}>+ New matter…</option>
        </select>
        {error && <span className="field-error">{error}</span>}
      </label>
      {creating && (
        <MatterFormModal
          projectId={projectId}
          onClose={() => setCreating(false)}
          onSaved={(m) => onChange(m.id)}
        />
      )}
    </>
  );
}
